/* eslint-disable no-console */
/* eslint-disable react/jsx-no-bind */
import _ from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Typography } from '@material-ui/core';
import ResponsiveContainer from '../ResponsiveContainer';
import {
  displayChildAge, displayFacilityType, displayFamilyType, composeWhereClause,
} from '../../modules/data';
import { rdsSelect, rdsTabulate } from '../../modules/rds';
import states, { stateFIPS } from '../../modules/states';
import { toCurrency, toPercent } from '../../modules/text';
import CountyContext from '../../contexts/CountyContext';
import FiltersContext from '../../contexts/FiltersContext';
import StateCountyMap from '../StateCountyMap';
import StoryContext from '../../contexts/StoryContext';
import { getMedianHouseholdIncome, getRatesByState } from './data';

function CountyMap() {
  const { stateCode } = useParams();
  const {
    primaryColor, lightColor, darkColor, selectionColor, hoverColor,
  } = useContext(StoryContext);
  const {
    county, countyCode, setCounty, setCountyCode,
  } = useContext(CountyContext);

  const { familyType, settingType, childAge } = useContext(FiltersContext);

  const [data, setData] = useState([]);
  const [dataRange, setDataRange] = useState([]); // range of data for the map to calculate gradients
  const [error, setError] = useState(null);
  const [, setReady] = useState(false);
  const [incomeAttribute, setIncomeAttribute] = useState('inc_hh');

  useEffect(() => {
    // console.log(`family type changed to ${familyType}. Recalculating...`);
    if (familyType === 'All') {
      setIncomeAttribute('inc_hh');
    }
    if (familyType === 'Married-couple family') {
      setIncomeAttribute('inc_fam_married');
    }

    if (familyType === 'Single-parent family') {
      setIncomeAttribute('inc_fam_nospouse');
    }
  }, [familyType]);

  useEffect(() => {
    function getRatesByCounty() {
      let whereClause = `state_fips=${stateFIPS[stateCode.toUpperCase()]}`;
      whereClause += composeWhereClause({ settingType, childAge });

      return rdsTabulate('ccdc', 'county_setting_group_rate_annual', {
        dims: 'county_fips',
        measure: 'average_rate:AVG(rate)',
        where: whereClause,
        format: 'amcharts',
        inject: false,
        orderby: 'county_fips',
      }, true);
    }

    function getCountyMedianHouseholdIncome() {
      return rdsSelect('3rdparty', 'acs_sams_county_wide', {
        cols: 'county_fips,inc_hh,inc_fam_married,inc_fam_nospouse',
        where: `state_fips=${stateFIPS[stateCode.toUpperCase()]} AND (county_suppressed=0 OR county_suppressed=NULL)`,
        format: 'amcharts',
        inject: false,
        orderby: 'county_fips',
      });
    }

    // need data for state rate, county income, state income
    if (stateCode && states[stateCode.toUpperCase()]) {
      setReady(false);
      Promise.all([getRatesByCounty(), getRatesByState(stateCode, null, { settingType, childAge }), getCountyMedianHouseholdIncome(), getMedianHouseholdIncome(stateCode)])
        .then((responses) => {
          const d = [];
          const countyRates = responses[0].parsedBody.dataProvider;
          const stateRate = responses[1].parsedBody.dataProvider[0];
          const countyIncomes = responses[2].parsedBody.dataProvider;
          const stateIncomes = responses[3].parsedBody.dataProvider[0];

          const stateData = { ...stateRate, ...stateIncomes };
          const dr = []; // array of values to be used to color the map. Data range.

          countyRates.forEach((rate) => {
            const countyIncome = _.find(countyIncomes, { county_fips: rate.county_fips });
            const dataPoint = { ...rate, ...countyIncome }; // merge rate and income data
            dataPoint.stateData = stateData; // add state data

            if (dataPoint[incomeAttribute]) {
              dataPoint.value = dataPoint.average_rate / dataPoint[incomeAttribute]; // value used by map to color
              dr.push(dataPoint.value);
            }
            d.push(dataPoint);
          });
          setData(d);
          setDataRange(dr);
        })
        .catch((err) => {
          setError(err);
        })
        .finally(() => {
          setReady(true);
        });
    }
  }, [stateCode, settingType, childAge]);

  useEffect(() => {
    // console.log(`income attribute changed to ${incomeAttribute}. Recalculating...`);
    const dr = [];
    const d = data.map((dataPoint) => {
      const dp = { ...dataPoint };
      if (dp[incomeAttribute]) {
        dp.value = dp.average_rate / dp[incomeAttribute];
        dr.push(dp.value);
      }
      return dp;
    });
    setData(d);
    setDataRange(dr);
  }, [incomeAttribute]);


  const toolTipFormatterFunction = function toolTipFormatterFunction(countyMapDatapoint, dataPoint) {
    if (!dataPoint) {
      return 'No data';
    }

    try {
      const income = dataPoint[incomeAttribute];
      const stateIncome = dataPoint.stateData[incomeAttribute];

      let result = `In <b>${countyMapDatapoint.properties.name} ${countyMapDatapoint.properties.TYPE}, ${countyMapDatapoint.properties.STATE}</b>, the average monthly price
        of full-time child care is <b>${toCurrency(dataPoint.average_rate / 12)}</b> per <b>${displayChildAge(childAge).toLowerCase()}</b> in 
        <b>${displayFacilityType(settingType).toLowerCase().replace('fcc', 'FCC')}</b>. (State price is <b>${toCurrency(dataPoint.stateData.average_rate / 12)}</b>)
        <br/><br/>`;

      if (income) {
        result += `That's <b>${toPercent(dataPoint.average_rate / income)}</b> of the median household income for a <b>${displayFamilyType(familyType).toLowerCase()}</b> with a child under 6. `;
      }

      result += `(State percent price is <b>${toPercent(dataPoint.stateData.average_rate / stateIncome)}</b>)`;

      return result;
    } catch (e) {
      console.error(e);
      console.info(countyMapDatapoint);
      console.info(dataPoint);
    }
    return 'N/A';
  };

  const legendValueFormatter = function legendValueFormatter(v) {
    return Number.parseInt(v * 100, 10);
  };

  const legendLabelFormatter = function legendLabelFormatter(v) {
    return toPercent(v);
  };

  return (
    <>
      {error && <Typography variant="body1" color="error">{error.message}</Typography>}
      <ResponsiveContainer aspect={1.0} width="100%">
        <StateCountyMap
          data={data}
          dataRange={dataRange}
          legendValueFormatter={legendValueFormatter}
          legendLabelFormatter={legendLabelFormatter}
          legendTitleText={`The darker the color, the greater the percent of the median household income for a ${displayFamilyType(familyType).toLowerCase()} spent on ${displayFacilityType(settingType).toLowerCase().replace('fcc', 'FCC')} per ${displayChildAge(childAge).toLowerCase()}`}
          stateCode={stateCode}
          colorHigh={darkColor}
          colorLow={lightColor}
          primaryColor={primaryColor}
          selectionColor={selectionColor}
          hoverColor={hoverColor}
          setCounty={setCounty}
          setCountyCode={setCountyCode}
          county={county}
          countyCode={countyCode}
          toolTipFormatterFunction={toolTipFormatterFunction}
        />
      </ResponsiveContainer>
    </>
  );
}

export default CountyMap;
